import React, { useState } from "react";
import "./home_page_styles/homePage.css";
import { Link } from "react-router-dom";
import Fade from "react-reveal/Fade";
import {
  Target,
  Search,
  Award,
  Globe,
  Shield,
  Zap,
  TrendingUp,
  ShieldCheck,
} from "lucide-react";
import img1 from "./media/Jewelry Shops.jpeg";
import img2 from "./media/Gold Makers.jpg";
import img3 from "./media/Diamond Merchants.png";
import img4 from "./media/Gold Merchants-1.jpeg";
import img5 from "./media/Gold Importers.png";
import img6 from "./media/Gold Exporters.avif";

const useCases = [
  {
    id: 1,
    image: { src: img1, alt: "Jewelry Shops" },
    theme: "Enhancing Online Presence with .gold",
    description:
      "A jewelry shop can use a .gold domain website to showcase their collections, provide product information, and sell jewelry online. The .gold extension specifically targets the gold industry, improving brand recognition and establishing trust with customers. A well-designed website can also help the business stand out from competitors and attract new customers, driving sales and success.",
    venue: "Jewelry Shops",
  },
  {
    id: 2,
    image: { src: img2, alt: "Gold Makers" },
    theme: "Maximizing Online Impact with .gold Domain",
    description:
      "A gold maker can use a .gold domain website to provide product information, and sell their gold products online. The .gold extension specifically targets the gold industry, improving brand recognition and establishing trust with customers. A well-designed website can also help the business stand out from competitors and attract new customers, boosting sales in the gold market.",
    venue: "Gold Makers",
  },
  {
    id: 3,
    image: { src: img3, alt: "Diamond Merchants" },
    theme: "Enhancing Diamond Business with .gold",
    description:
      "A diamond merchant can use a .gold domain website to showcase their diamond collections, provide product information, and sell diamonds online. The .gold extension, although specifically targeted towards the gold industry, can still enhance the online presence and credibility of a diamond business.",
    venue: "Diamond Merchants",
  },
  {
    id: 4,
    image: { src: img4, alt: "Gold Merchants" },
    theme: "Leveraging .gold Domain for Gold Business",
    description:
      "A gold merchant can use a .gold domain website to showcase products, provide information, and sell gold online, improving brand recognition and customer trust. A user-friendly website can also help establish credibility and differentiate the business from competitors, increasing sales and boosting success in the competitive gold market.",
    venue: "Gold Merchants",
  },
  {
    id: 5,
    image: { src: img5, alt: "Gold Importers" },
    theme: "Boosting Gold Importation with .gold",
    description:
      "A gold importer can use a .gold domain website to display their inventory, share industry knowledge, and facilitate online sales. The .gold extension enhances their online presence, establishing credibility and demonstrating expertise in the gold industry. A well-designed website can also attract new customers, boost search engine rankings, and drive success for the gold import business.",
    venue: "Gold Importers",
  },
  {
    id: 6,
    image: { src: img6, alt: "Gold Exporters" },
    theme: "Expanding Gold Export with .gold",
    description:
      "A gold exporter can use a .gold domain website to showcase their products, provide information, and sell gold online to global markets. The .gold extension specifically targets the gold industry, improving brand recognition and establishing trust with customers. A well-designed website can also help establish credibility and differentiate the business from competitors, expanding sales and success in the international gold export market.",
    venue: "Gold Exporters",
  },
];

const benefits = [
  {
    icon: Target,
    title: "Industry Recognition",
    description:
      "Instantly communicate your gold industry focus to customers worldwide with a domain that speaks your business language.",
    delay: 0,
  },
  {
    icon: Search,
    title: "SEO Advantage",
    description:
      "Boost search rankings with keyword-rich domains that help customers find your gold business effortlessly.",
    delay: 100,
  },
  {
    icon: Award,
    title: "Brand Credibility",
    description:
      "Establish trust and authority in the precious metals market with a premium, professional web address.",
    delay: 200,
  },
  {
    icon: Globe,
    title: "Global Reach",
    description:
      "Break geographical barriers and connect with international gold buyers and sellers seamlessly.",
    delay: 300,
  },
  {
    icon: Shield,
    title: "Brand Protection",
    description:
      "Secure your digital identity and prevent competitors from claiming valuable domain names in your niche.",
    delay: 400,
  },
  {
    icon: Zap,
    title: "Memorable Impact",
    description:
      "Create lasting impressions with short, memorable domains that customers can easily recall and share.",
    delay: 500,
  },
];

function GoldDomain() {
  const [hoveredBenefit, setHoveredBenefit] = useState(null);

  return (
    <div className="gold-domain-page">
      {/* Hero Intro Section */}
      <div className="discover-part pt-[8rem] pb-[5rem]">
        <div className="container a-container a-padding">
          <div className="text-center max-w-5xl mx-auto">
            <h1 className="font-bold lg:text-6xl md:text-5xl text-4xl title mb-8 !leading-[120%]">
              Welcome to <span className="text-pent">.Gold Domain </span>
              Extensions
            </h1>
            <p className="text-lg md:text-xl paragraph mb-10 leading-relaxed text-gray-700">
              A .gold domain extension provides a profitable start for
              gold-based businesses by establishing a strong online presence,
              improving search engine rankings, and creating a memorable web
              address that stands out from competitors.
            </p>
            <div className="flex justify-center">
              <Link to="/Marketplace">
                <button className="bg-gradient-to-r from-[#fcdb6a] to-[#b99653] text-[#101c4c] px-10 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300">
                  Browse Premium Domains
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* What is .gold TLD Section */}
      <div className="py-[2rem] bg-[#eef4f9]">
        <div className="container a-container a-padding">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-bold lg:text-5xl md:text-4xl text-3xl mb-6 title">
                What is <span className="text-pent">.Gold</span> Top-Level
                Domain?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding the power and potential of gold-focused domain
                names
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div className="bg-white p-8 rounded-2xl border-l-4 border-[#fcdb6a] shadow-md">
                  <h3 className="font-bold text-2xl mb-4 text-[#101c4c]">
                    A Specialized Digital Identity
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-base">
                    The .gold domain extension is a specialized top-level domain
                    (TLD) specifically designed for businesses and individuals
                    operating in the gold industry. Launched to provide a unique
                    and recognizable digital identity, .gold domains instantly
                    convey your connection to the precious metals marketplace.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-2xl border-l-4 border-[#b99653] shadow-md">
                  <h3 className="font-bold text-2xl mb-4 text-[#101c4c]">
                    Industry-Focused Excellence
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-base">
                    Unlike generic domains like .com or .net, a .gold extension
                    immediately tells visitors that your business is dedicated
                    to gold products, services, or investments. This targeted
                    approach helps you stand out in search results and builds
                    instant credibility with potential customers who are
                    specifically looking for gold-related businesses.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-[#fcdb6a] to-[#b99653] p-8 rounded-2xl shadow-xl">
                  <h3 className="font-bold text-2xl mb-6 text-[#101c4c]">
                    Perfect For
                  </h3>
                  <ul className="space-y-4">
                    {[
                      "Gold Dealers & Retailers",
                      "Jewelry Manufacturers",
                      "Precious Metal Investors",
                      "Gold Mining Companies",
                      "Bullion Traders",
                      "Gold Market Analysts",
                      "Refineries & Suppliers",
                    ].map((item, index) => (
                      <li
                        key={index}
                        className="flex items-center text-[#101c4c] font-medium"
                      >
                        <span className="mr-3 text-xl">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Key Features */}
            <div className="mt-12 bg-[#101c4c] text-white p-10 rounded-2xl">
              <h3 className="font-bold text-3xl mb-8 text-center">
                Why <span className="text-[#fcdb6a]">.Gold</span> Makes a
                Difference
              </h3>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#fcdb6a] to-[#b99653] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-[#101c4c]" />
                  </div>
                  <h4 className="font-bold text-xl mb-3 text-[#fcdb6a]">
                    Premium Positioning
                  </h4>
                  <p className="text-gray-300">
                    Position your business as a premium player in the gold
                    industry with a distinctive domain.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#fcdb6a] to-[#b99653] rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-[#101c4c]" />
                  </div>
                  <h4 className="font-bold text-xl mb-3 text-[#fcdb6a]">
                    Market Visibility
                  </h4>
                  <p className="text-gray-300">
                    Increase your visibility in a competitive market with a
                    domain that stands out.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#fcdb6a] to-[#b99653] rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShieldCheck className="w-8 h-8 text-[#101c4c]" />
                  </div>
                  <h4 className="font-bold text-xl mb-3 text-[#fcdb6a]">
                    Trust & Authority
                  </h4>
                  <p className="text-gray-300">
                    Build immediate trust with customers through a professional,
                    industry-specific domain.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-[2rem] bg-white">
        <div className="container a-container a-padding">
          <div className="text-center mb-16">
            <h2 className="font-bold lg:text-5xl md:text-4xl text-3xl mb-4 title">
              Key <span className="text-pent">Benefits</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transform your business presence with a premium .gold domain
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              const isHovered = hoveredBenefit === index;

              return (
                <div
                  key={index}
                  onMouseEnter={() => setHoveredBenefit(index)}
                  onMouseLeave={() => setHoveredBenefit(null)}
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${benefit.delay}ms both`,
                  }}
                  className={`
                    bg-[#eef4f9] p-8 rounded-xl transition-all duration-300
                    ${
                      isHovered
                        ? "shadow-2xl -translate-y-2 bg-gradient-to-br from-[#fcdb6a] to-[#b99653]"
                        : "shadow-md"
                    }
                  `}
                >
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 transition-all ${
                      isHovered
                        ? "bg-white"
                        : "bg-gradient-to-r from-[#fcdb6a] to-[#b99653]"
                    }`}
                  >
                    <IconComponent
                      className={`w-8 h-8 ${
                        isHovered ? "text-[#101c4c]" : "text-white"
                      }`}
                    />
                  </div>
                  <h3 className="font-bold text-2xl mb-4 text-[#101c4c]">
                    {benefit.title}
                  </h3>
                  <p
                    className={`leading-relaxed ${
                      isHovered ? "text-[#101c4c]" : "text-gray-700"
                    }`}
                  >
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Who Can Use Section WITH Use Cases - Combined */}
      <div className="py-[2rem] bg-[#eef4f9]">
        <div className="container a-container a-padding">
          {/* Header */}
          <div className="text-center max-w-5xl mx-auto mb-16">
            <h2 className="font-bold lg:text-5xl md:text-4xl text-3xl mb-6 title">
              Who Can Use a <span className="text-pent">.Gold Domain</span>?
            </h2>
            <p className="text-lg paragraph text-gray-700 leading-relaxed">
              Transform your gold business with a domain that instantly
              communicates trust, expertise, and premium quality to customers
              worldwide.
            </p>
          </div>

          {/* Use Cases - Right Below Header */}
          <Fade cascade>
            <div className="space-y-16">
              {useCases.map(
                (
                  { image: { src, alt }, venue, theme, description, id },
                  index
                ) => {
                  const isEven = index % 2 === 0;
                  return (
                    <div
                      key={id}
                      className={`flex flex-col ${
                        isEven ? "lg:flex-row" : "lg:flex-row-reverse"
                      } gap-8 items-center`}
                    >
                      {/* Image Section */}
                      <div className="w-full lg:w-1/2">
                        <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                          <img
                            src={src}
                            alt={alt}
                            className="w-full h-[400px] object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                          <div className="absolute bottom-6 left-6">
                            <span className="inline-block px-6 py-3 bg-gradient-to-r from-[#fcdb6a] to-[#b99653] text-[#101c4c] font-bold rounded-full">
                              {venue}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="w-full lg:w-1/2">
                        <h3 className="font-bold text-3xl lg:text-4xl mb-6 text-[#101c4c] leading-tight">
                          {theme}
                        </h3>
                        <p className="text-gray-700 text-lg leading-relaxed">
                          {description}
                        </p>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </Fade>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-[2rem] bg-[#101c4c]">
        <div className="container a-container a-padding">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="font-bold lg:text-5xl md:text-4xl text-3xl mb-6 gold-main leading-tight">
              Ready to Elevate Your Gold Business?
            </h2>
            <p className="text-lg md:text-xl mb-10 text-white leading-relaxed font-medium">
              Browse our premium marketplace to find the perfect .gold domain
              for your business. All domains are carefully curated, verified,
              and ready for immediate use.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/Marketplace">
                <button className="w-full bg-gold-dark-gradient sm:!w-fit white-btn">
                  Explore Available Domains
                </button>
              </Link>
              <Link to="/sell-your-dot-gold-domain">
                <button className="w-full bg-gold-dark-gradient sm:!w-fit white-btn">
                  List Your Domain
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default GoldDomain;
